import axios, { AxiosResponse, AxiosError } from 'axios';
import Cookies from 'js-cookie';
import i18n from '@/utils/i18n';
import { getDeviceId } from '@/utils/logReporter';

// const token = Cookies.get(import.meta.env.VITE_COOKIE_SUFFIX as String);
console.log('import.meta.env.DEV >>', import.meta.env);

let token = window.crypto.randomUUID();
function getToken() {
  if (!window.location.pathname.includes('/share')) {
    token = Cookies.get(import.meta.env.VITE_COOKIE_SUFFIX as String) || token;
  }
  console.log('token >>', token);
  return token;
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_FELO_SUBTITLES_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
    Authorization: getToken(),
  },
});

instance.interceptors.request.use(
  (config) => {
    // 处理重复请求
    config.headers.Authorization = getToken();

    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);

export const getProductList = () =>
  instance
    .request<API.Response<API.ProductListResponse>>({
      method: 'get',
      url: '/api/product/list',
    })
    .then((res) => {
      const { product_list, product_price_list, product_item_list } = res.data.data;

      return {
        ...res.data,
        data: product_list.map((product) => ({
          ...product,
          price_list: product_price_list.filter((price) => price.product_id === product.product_id),
          product_item: product_item_list.find((items) => items.product_id === product.product_id),
        })),
      };
    });

// 获取完整的产品列表，包含价格和附加信息
export const getProductListWithPricing = () => getProductList();

// 获取订阅产品列表
export const getSubscriptionProducts = () =>
  instance
    .request<API.Response<API.ProductListResponse>>({
      method: 'get',
      url: '/api/product/list',
      params: {
        product_type: 'SUBSCRIPTION',
      },
    })
    .then((res) => {
      const { product_list, product_price_list, product_item_list } = res.data.data;

      return {
        ...res.data,
        data: product_list.map((product) => ({
          ...product,
          price_list: product_price_list.filter((price) => price.product_id === product.product_id),
          product_item: product_item_list.find((items) => items.product_id === product.product_id),
        })),
      };
    });

// 夏季促销活动信息（后台必登录，Authorization 按账号判定资格即可）。
// 注意：不要带自定义 deviceId 头——本站到 subtitle-api 是跨域请求，
// 服务端 CORS allow-headers 白名单没有 deviceId，会导致预检失败、整个请求被浏览器拦截。
export const getSummerPromotionInfo = () =>
  instance
    .request<API.Response<API.SummerPromotionInfo>>({
      method: 'get',
      url: '/api/promotion/summer/info',
    })
    .then((res) => res.data);

// 首月半价活动信息。自定义 deviceId 头会被 CORS 预检拦截（白名单没有它），
// 设备号经 query 参数传递（不触发预检）；设备标识是 .felo.me 共享 cookie，与官网一致，
// 服务端据此绑定设备-账号关系（同设备多账号防薅）
export const getFirstMonthPromotionInfo = () =>
  instance
    .request<API.Response<API.FirstMonthPromotionInfo>>({
      method: 'get',
      url: '/api/promotion/first-month/info',
      params: { device_id: getDeviceId() },
    })
    .then((res) => res.data);

export const createOrder = (product_id: string, currency: string, utm_source?: string) =>
  instance.request<API.Response<string>>({
    method: 'POST',
    url: '/api/checkout',
    data: {
      cancel_url: `${window.location.origin}/account/?payment=failed&product_id=${product_id}`,
      currency,
      product_id,
      success_url: `${window.location.origin}/account/?payment=succeed&product_id=${product_id}`,
      utm_source,
      // 设备号随单传给服务端，下单侧兜底绑定设备（首月半价设备防线）
      device_id: getDeviceId(),
    },
  });

export const completeOrder = (checkout_id: string, receipt: string) =>
  instance.request<API.Response<unknown>>({
    method: 'PUT',
    url: `/api/checkout/${checkout_id}`,
    params: {
      receipt,
    },
  });

export const cancelOrder = (checkout_id: string) =>
  instance.request<API.Response<unknown>>({
    method: 'DELETE',
    url: `/api/checkout/${checkout_id}`,
  });

// 取消订阅
export const cancelSubscription = (subscriptionId: string) =>
  instance.request<API.Response<unknown>>({
    method: 'GET',
    url: '/api/subscription/cancel',
    params: {
      subscriptionId,
    },
  });

// 恢复订阅
export const restoreSubscription = (subscriptionId: string) =>
  instance.request<API.Response<unknown>>({
    method: 'PUT',
    url: '/api/subscription/restore',
    data: {
      subscriptionId,
    },
  });

// 获取Stripe订阅管理地址
export const getStripePortalUrl = (return_url: string) =>
  instance.request<API.Response<string>>({
    method: 'GET',
    url: '/api/subscription/portal',
    params: {
      return_url,
    },
  });

// 当前余额
export const getCurrentPlan = () =>
  instance.request<API.Response<API.PlanResponse>>({
    url: '/api/plan/current',
  });

// 获取用户当前计划详情
export const getCurrentPlanNew = () =>
  instance.request<API.Response<API.CurrentPlanNewResponse>>({
    url: '/api/plan/new/current',
    method: 'GET',
  });

// 判断当前用户是否为企业用户
export const checkEnterpriseUser = () =>
  instance.request<API.Response<API.EnterpriseCheckResponse>>({
    url: '/api/user/enterprise/check',
    method: 'GET',
  });

// 按量用户本月已用时长
export const getMonthlySummary = () =>
  instance.request<API.Response<API.MonthlySummaryResponse>>({
    url: '/api/plan/monthly/summary',
    method: 'GET',
  });

// 当前购买/赠送余额
export const getPlanBalance = () =>
  instance.request<API.Response<API.PlanBalanceResponse>>({
    url: '/api/plan/balance',
  });
// 查询邀请信息
export const getInvitationInfo = () =>
  instance.request<API.Response<API.InviteInfo>>({
    url: '/api/user/invitation',
  });
// 购买记录
export const getPlanList = () =>
  instance.request<API.Response<API.PlanListResponse>>({
    url: '/api/plan/detail.list',
  });
// 购买记录
export const getBoughtProductList = (current: number, size: number, source?: string) =>
  instance.request<API.Response<API.BoughtProductResponse>>({
    url: '/api/user/product/page',
    params: {
      current,
      size,
    },
  });

// 使用记录
export const getUsageList = (current: number, size: number, start?: string, end?: string) =>
  instance.request<API.Response<API.DeductionResponse>>({
    url: '/api/deduction/page',
    params: {
      current,
      size,
    },
  });

// 用户设置
export const getUserConfig = (key?: string) =>
  instance
    .request<API.Response<API.UserConfigResponse>>({
      url: '/api/user/config',
      params: {
        key,
      },
    })
    .then((res) => {
      if (res.data.data) {
        Object.keys(res.data.data).forEach((k) => {
          // @ts-ignore
          res.data.data[k] = JSON.parse(res.data.data[k]).value;
        });
      }

      return res;
    });

// 修改字幕设置
export const updateUserConfig = (key: string, data: any) =>
  instance.request<API.Response<unknown>>({
    method: 'POST',
    url: '/api/user/config/set',
    data: {
      config_type: key,
      data: JSON.stringify(data),
    },
  });

// 会员中心界面语言(i18n.language: ja/zh-CN/zh-TW/en)映射为后端语言码(SubtitleLangEnum)
// 用于会议标题按界面语言本地化(后端 TitleI18nComponent 读取时翻译并缓存)
const toBackendLang = (): string => {
  const map: Record<string, string> = {
    'zh-CN': 'zh_hans',
    'zh-TW': 'zh_hant',
    ja: 'ja',
    'ja-JP': 'ja',
    en: 'en',
    'en-US': 'en',
  };
  return map[i18n.language] || 'en';
};

// 用户字幕块查询
export const getLogDetail = (id: string) =>
  instance.request<API.Response<API.LogDetailResponse>>({
    url: `/api/user/share/subtitle/segment/${id}`,
    params: {
      lang: toBackendLang(),
    },
  });

// 用户字幕块分页查询
export const getLogPage = (current: number, size: number) =>
  instance.request<API.Response<API.LogListResponse>>({
    url: '/api/user/subtitle/segment/page',
    params: {
      current,
      size,
      lang: toBackendLang(),
    },
  });

// 导出字幕
export const downloadLog = (data: { user_subtitle_segment_id: string; zone: string }) =>
  instance.request<API.Response<string>>({
    method: 'PUT',
    url: '/api/user/subtitle/segment/export',
    data,
  });

// 删除字幕
export const deleteLog = (id: string) =>
  instance.request<API.Response<unknown>>({
    method: 'DELETE',
    url: `/api/user/subtitle/segment/${id}`,
  });

// 用户字幕标题修改
export const updateLogTitle = (data: { user_subtitle_segment_id: string; title: string }) =>
  instance.request<API.Response<unknown>>({
    method: 'PUT',
    url: '/api/user/subtitle/segment/title',
    data,
  });

// 更新字幕原文并重新翻译
export const updateOriginalText = (data: API.SegmentRetranslationParams) =>
  instance.request<API.Response<API.SegmentRetranslationResult>>({
    method: 'PUT',
    url: '/api/user/subtitle/segment/retranslation',
    data,
  });

// 提交反馈到邮件渠道（CONNECTION），与官网 お問い合わせ 统一
export const sendContactEmail = (data: {
  name: string;
  email: string;
  company: string;
  type: string;
  subject: string;
  content: string;
}) =>
  instance.request<API.Response<unknown>>({
    method: 'POST',
    url: '/api/send/email',
    data: {
      email_type_enum: 'CONNECTION',
      content: JSON.stringify(data),
    },
  });

// 获取用户签名
export const uploadImageSign = (data: { suffix: string }) =>
  instance.request<API.Response<any>>({
    method: 'POST',
    url: '/api/user/feedback/uploadImageSign',
    data,
  });

// 获取 字幕详情页面 MQTT 链接信息
export const getWebsiteMqttInfo = (room_id: string) =>
  instance.request<API.Response<API.WebsiteMqttInfoResponse>>({
    method: 'POST',
    url: '/api/connection/websiteInfo',
    data: {
      client_type: 'WEB',
      room_id,
    },
  });

// 包装函数
export const wrapRequest = async <T, P>(request: (params: P) => Promise<AxiosResponse<API.Response<T>>>, params: P) => {
  try {
    const response = await request(params);
    return { code: 'succ', data: response.data };
  } catch (error) {
    // 处理异常
    const res: any = (error as AxiosError).response?.data;
    console.error('Request failed:', error);
    return {
      code: res.code ? (res.code as string) : 'fail',
      data: null,
    };
  }
};

// 获取keywords列表
export const fetchKeyWordList = () =>
  instance.request<API.Response<API.FetchKeyWordListResult>>({
    method: 'POST',
    url: '/api/user/noun/list',
  });

// Save：keywords列表
export const saveKeyWordList = (data: API.SaveKeyWordListParams) =>
  instance.request<API.Response<API.SaveKeyWordListResult>>({
    method: 'POST',
    url: '/api/user/noun/save',
    data,
  });

// Update：keywords列表
export const updateKeyWordList = (data: API.UpdateKeyWordListParams) =>
  instance.request<API.Response<API.UpdateKeyWordListResult>>({
    method: 'POST',
    url: '/api/user/noun/update',
    data,
  });
// Delete：keywords列表
export const deleteKeyWordList = (data: API.DelKeyWordListParams) =>
  instance.request<API.Response<API.DelKeyWordListResult>>({
    method: 'POST',
    url: '/api/user/noun/delete',
    data,
  });

// 更新keywords列表状态：keywords列表
export const setKeyWordListStatus = (data: API.SetKeyWordListStatusParams) =>
  instance.request<API.Response<API.SetKeyWordListStatusResult>>({
    method: 'POST',
    url: '/api/user/noun/config/update',
    data,
  });

// 获取术语库列表
export const getTermBaseList = () =>
  instance.request<API.Response<API.TermBaseListResponse>>({
    method: 'GET',
    url: '/api/noun/library/query',
  });

// 创建术语库
export const createTermBase = (data: API.CreateTermBaseParams) =>
  instance.request<API.Response<API.CreateTermBaseResult>>({
    method: 'POST',
    url: '/api/noun/library/create',
    data,
  });

// 更新术语库
export const updateTermBase = (data: API.UpdateTermBaseParams) =>
  instance.request<API.Response<API.UpdateTermBaseResult>>({
    method: 'POST',
    url: '/api/noun/library/update',
    data,
  });

// 删除术语库
export const deleteTermBase = (data: API.DeleteTermBaseParams) =>
  instance.request<API.Response<API.DeleteTermBaseResult>>({
    method: 'POST',
    url: '/api/noun/library/delete',
    data,
  });
// 术语库详情
export const getTermBaseDetailInfo = (data: API.GetTermBaseDetailParams) =>
  instance.request<API.Response<API.TermBaseDetail>>({
    method: 'GET',
    url: '/api/noun/library/query/detail',
    params: {
      noun_library_id: data.noun_library_id,
    },
  });

// 获取术语详情列表
export const getTermBaseDetailList = (data: API.GetTermBaseDetailParams) =>
  instance.request<API.Response<API.GetTermBaseDetailResult>>({
    method: 'POST',
    url: '/api/translation/noun/query',
    data,
  });

// 新增术语
export const updateTermBaseDetail = (data: API.UpdateTermBaseDetailParams) =>
  instance.request<API.Response<API.UpdateTermBaseDetailResult>>({
    method: 'POST',
    url: '/api/translation/noun/update',
    data,
  });

// 删除术语
export const deleteTermBaseDetail = (data: API.DeleteTermBaseDetailParams) =>
  instance.request<API.Response<API.DeleteTermBaseDetailResult>>({
    method: 'POST',
    url: '/api/translation/noun/delete',
    data,
  });

// 批量上传术语
export const uploadTermBase = (data: API.UploadTermBaseParams) => {
  const formData = new FormData();
  // 假设 data 中包含文件和其他必要参数
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key as keyof API.UploadTermBaseParams]);
  });

  return instance.request<API.Response<API.UploadTermBaseResult>>({
    method: 'POST',
    url: '/api/translation/noun/csv/upload',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// 获取分享房间ID
export const getShareRoomId = ({ userId, roomId }: { userId: string | undefined; roomId: string | undefined }) =>
  instance.request<API.Response<API.ShareRoomIdResponse>>({
    method: 'GET',
    url: `/api/user/share/query/${userId}`,
    params: {
      room_id: roomId,
    },
  });

// 获取分享连接
export const getShareConnect = (room_id: string) =>
  instance.request<API.Response<API.ShareConnectResponse>>({
    method: 'POST',
    url: '/api/connection/shareInfo',
    data: {
      room_id,
      client_type: 'WEB',
    },
  });

export const getSummary = (data: {
  user_subtitle_segment_id: string;
  target_lang: string;
  summary_prompt_type_enum: string;
}) =>
  instance.request<API.Response<API.SummaryResponse>>({
    method: 'POST',
    url: '/api/app/summary/create',
    data,
  });

// 获取历史
export const getHistory = (data: { user_subtitle_segment_id: string }) =>
  instance.request<API.Response<API.HistoryResponse>>({
    method: 'POST',
    url: '/api/app/summary/queryDetail',
    data,
  });

// 获取Gemini文件
export const getGeminiFile = (data: { roomId: string }) =>
  instance.request<API.Response<API.GeminiFileResponse>>({
    method: 'GET',
    url: '/api/gemini/file/merge',
    params: {
      room_id: data.roomId,
    },
  });

// 根据Gemini文件生成结果
export const createGeminiResult = (data: {
  file_merge_id: string;
  prompt_type: 'TRANSCRIPTION_PROMPT' | 'SUMMARY_PROMPT';
  target_lang: string;
}) =>
  instance.request<API.Response<API.GeminiResultResponse>>({
    method: 'POST',
    url: '/api/app/gemini/generate/create',
    data,
  });

export const getJobsStatus = (data: {
  file_merge_id: string;
  prompt_type: 'SUMMARY_PROMPT' | 'TRANSCRIPTION_PROMPT' | 'TRANSLATE_PROMPT';
}) =>
  instance.request<API.Response<API.GeminiJobStatusResponse>>({
    method: 'POST',
    url: '/api/app/summary/job/query',
    data: {
      file_merge_id: data.file_merge_id,
      prompt_type: data.prompt_type,
    },
  });

// 查询Gemini历史结果
const geminiHistoryUrl = {
  SUMMARY_PROMPT: '/api/app/summary/gemini/query',
  TRANSCRIPTION_PROMPT: '/api/app/transcription/gemini/query',
  TRANSLATE_PROMPT: '/api/app/translate/gemini/query',
};
export const queryGeminiSummaryResult = (data: { file_merge_id: string }) =>
  instance.request<API.Response<API.GeminiSummaryResultResponse>>({
    method: 'POST',
    url: geminiHistoryUrl.SUMMARY_PROMPT,
    data: {
      file_merge_id: data.file_merge_id,
    },
  });

// 查询Gemini历史结果
export const queryGeminiResult = (data: {
  file_merge_id: string;
  prompt_type: 'SUMMARY_PROMPT' | 'TRANSCRIPTION_PROMPT' | 'TRANSLATE_PROMPT';
}) =>
  instance.request<API.Response<API.GeminiTranscriptionResultResponse>>({
    method: 'POST',
    url: geminiHistoryUrl[data.prompt_type],
    data: {
      file_merge_id: data.file_merge_id,
    },
  });

// 查询文件状态
export const getFileStatus = (data: { file_merge_id: string }) =>
  instance.request<API.Response<API.FileStatusResponse>>({
    method: 'GET',
    url: '/api/gemini/file/status',
    params: {
      file_merge_id: data.file_merge_id,
    },
  });

// 结束音频合并
export const endGeminiMerge = (roomId: string) =>
  instance.request<API.Response<API.EndGeminiMergeResponse>>({
    method: 'POST',
    url: '/api/gemini/file/merge',
    data: {
      room_id: roomId,
    },
  });

// 创建Gemini总结任务
export const createGeminiSummaryJob = (
  fileMergeId: string,
  targetLang: string,
  promptTypeList: string[] = ['TRANSCRIPTION_PROMPT', 'SUMMARY_PROMPT', 'TRANSLATE_PROMPT'],
) =>
  instance.request<API.Response<API.CreateGeminiSummaryJobResponse>>({
    method: 'POST',
    url: '/api/app/summary/job/create',
    data: {
      file_merge_id: fileMergeId,
      prompt_type_list: promptTypeList,
      target_lang: targetLang,
    },
  });

// 更新用户设置
export const updateUserSettings = (settings: {
  productUpdates?: boolean;
  emailBillingNotifications?: boolean;
  usageLimitAlerts?: boolean;
  usageAnalytics?: boolean;
  marketingEmails?: boolean;
}) =>
  instance.request<API.Response<unknown>>({
    method: 'PUT',
    url: '/api/user/settings',
    data: settings,
  });

// 提交订阅取消反馈
export const createSubscriptionFeedback = (data: API.SubscriptionFeedbackRequest) =>
  instance.request<API.Response<any>>({
    method: 'post',
    url: '/api/user/feedback/subscription/create',
    data,
  });

// 获取分享配置（桌面端语言列表、附加语言限制等）
export const getShareConfig = (params: { roomId: string }) =>
  instance.request<API.Response<API.ShareConfigResp>>({
    method: 'GET',
    url: '/api/share/config',
    params,
  });

// share 观众订阅翻译语言（在 share 页面打开/切换语言时调用）
export const subscribeShareLang = (params: API.ShareLangParams) =>
  instance.request<API.Response<unknown>>({
    method: 'POST',
    url: '/api/share/subscribe-lang',
    data: params,
  });

// share 观众取消订阅（页面关闭时 best-effort）
export const unsubscribeShareLang = (params: API.ShareLangParams) =>
  instance.request<API.Response<unknown>>({
    method: 'DELETE',
    url: '/api/share/subscribe-lang',
    data: params,
  });

// share 观众心跳（2 分钟一次刷新 TTL）
export const heartbeatShareLang = (params: API.ShareLangParams) =>
  instance.request<API.Response<unknown>>({
    method: 'POST',
    url: '/api/share/subscribe-lang/heartbeat',
    data: params,
  });

// 翻译转录内容（AISidebar 翻译 tab）
// file_merge_id 可选:多模态记录传入后,后端优先用 Gemini 转录(带声纹分离的 speaker)做翻译源
export const translateText = (data: { user_subtitle_segment_id: string; target_lang: string; file_merge_id?: string }) =>
  instance.request<API.Response<{ translate_status?: 'DONE' | 'GENERATING'; generate_transcription_list: { speaker_id: string; transcription: string; original?: string; begin_time?: number; end_time?: number }[] }>>({
    method: 'POST',
    url: '/api/app/text/translate',
    data,
  });

export default instance;
